import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import { League } from '../../../sdk/model';
import { Modal, Typography, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Box, Button, Tabs, Tab } from '@mui/material';
import { displayDraftPick } from '../../util/DraftPickDisplay';
import DraftPickCard from '../../cards/draftPickCard';
import TeamDisplay from '../team/TeamDisplay';
import { toSalary } from '../../util/SalaryDisplay';

function LeagueDraftPicks() {
  const {leagueId} = useParams();
  
  const user = useAppSelector((state) => state.user);
  
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [viewingPick, setViewingPick] = React.useState(undefined);
  const [view, setView] = React.useState('all');
  const [roundNumber, setRoundNumber] = React.useState(1);
  
  // League Information
  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicAuctionData: false, 
    loadFullAuctionData: false, loadBasicDraftData: true, loadContractData: true, loadRosterData: true,
    setLoadComplete: setLoadComplete, isLoadComplete: isLoadComplete,
    setIsException: setIsException, isException: isException});
    
  if (!isLoadComplete) {
    return <p>Loading...</p>
  }
  
  const teamMap = new Map(league?.teams.map(team => [team.id, team]));
  const draftPickSeasons = Array.from(new Set(league?.teams.flatMap(team => team.draftPicks?.map(pick => pick.seasonYear))));
  const pickNumbersSet = league?.teams.some(team => team.draftPicks?.some(pick => pick.pickNumber));
  
  return <Box>
    <Modal open={viewingPick} onClose={() => setViewingPick(undefined)} 
        aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
        sx={{margin: '5%'}}>
      <DraftPickCard pick={viewingPick} leagueId={league?.id} />
    </Modal>
    <Typography variant="h6" component="div" color="text.secondary" sx={{ flexGrow: 1 }}>
      Draft Picks
    </Typography>
    {pickNumbersSet && <Tabs value={view} onChange={(event, value) => {
              setView(value)
              }} aria-label="Draft Pick Tabs">
      <Tab label="All Drafts" value="all" />
      <Tab label="Upcoming Draft" value="upcoming" />
    </Tabs>}
    {view === 'all' && <TableContainer className="centered" component={Paper}><Table>
      <TableHead><TableRow>
        <TableCell>Team</TableCell>
        {draftPickSeasons.map(season => <TableCell key={season}>{season}</TableCell>)}
      </TableRow></TableHead>
      <TableBody>
        {league?.teams.map(team => <TableRow key={team.id}>
          <TableCell>{team.name}</TableCell>
          {draftPickSeasons.map(season => {
            const picksForSeason = team.draftPicks!.filter(pick => pick.seasonYear === season);
            return <TableCell key={season}>
              {picksForSeason.length ?
                picksForSeason.map(pick => {
                  const originalTeam = teamMap.get(pick.originalTeamId);
                  return <p><Button onClick={() => setViewingPick(pick)}>{displayDraftPick(pick, originalTeam)}</Button></p>
                }) : "No picks"
              }
            </TableCell>
          })}
        </TableRow>)}
      </TableBody>
    </Table></TableContainer>}
    {view === 'upcoming' && <Box>
      <Tabs value={roundNumber} onChange={(event, value) => {
                setRoundNumber(value)
                }} aria-label="Draft Pick Tabs">
        {Array.from(Array(league?.currentSeason?.draft?.rounds!).keys()).map(roundIndex =>
          <Tab label={"Round " + (roundIndex + 1)} value={roundIndex + 1} />
        )}
      </Tabs>
      <TableContainer className="centered" component={Paper}><Table>
        <TableHead><TableRow>
          <TableCell>Pick Number</TableCell>
          <TableCell>Team</TableCell>
          <TableCell>Original Team</TableCell>
          <TableCell>{league?.currentSeasonYear} Value</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {league?.currentSeason?.draft?.draftPicks![roundNumber - 1].map(draftPick => {
            const team = teamMap.get(draftPick.teamId);
            const originalTeam = teamMap.get(draftPick.originalTeamId);
            return <TableRow key={draftPick.pickNumber}>
              <TableCell>
                <Button onClick={() => setViewingPick(draftPick)}>{draftPick.pickNumber}</Button>
              </TableCell>
              <TableCell>
                <TeamDisplay leagueId={leagueId} team={team} emphasized={false} teamId={draftPick.teamId} />
              </TableCell>
              <TableCell>
                <TeamDisplay leagueId={leagueId} team={originalTeam} emphasized={false} teamId={draftPick.originalTeamId} />
              </TableCell>
              <TableCell>
                {toSalary(draftPick.contract?.seasons[0].annual_salary)}
              </TableCell>
            </TableRow>
          })}
        </TableBody>
      </Table></TableContainer>
    </Box>}
  </Box>
}

export default LeagueDraftPicks;